/**
 * Session-scoped CSRF token reuse for the control-plane clients.
 *
 * Why reuse is safe here — verified against the server, not assumed. The API
 * registers `@fastify/csrf-protection` with `sessionPlugin: "@fastify/cookie"`
 * (apps/api/src/server.ts), so:
 *
 * - `reply.generateCsrf()` reads the *secret* back out of the signed, httpOnly,
 *   SameSite=strict cookie and only mints a new secret when that cookie is
 *   absent. A fresh call therefore does not rotate anything.
 * - The returned string differs per call only because each token embeds a
 *   random salt. Verification is `tokens.verify(secret, token)`, which re-derives
 *   the hash from the salt carried inside the submitted token — so every token
 *   minted from the live secret verifies. The server keeps no used-token set,
 *   and the plugin is registered without `csrfOpts.validity`, so tokens carry no
 *   expiry of their own.
 *
 * The protection is that a cross-origin attacker cannot read the httpOnly
 * secret cookie and therefore cannot mint a verifying token. How often the
 * legitimate page asks for one does not enter into that — reuse removes a round
 * trip without relaxing the double-submit guarantee.
 *
 * When the secret *does* change (cookie cleared, new browser session, a new
 * cookie secret on the API), the cached token stops verifying and the API
 * answers 403 `csrf_validation_failed` from its error handler. The request was
 * rejected in a preHandler, so it never reached a handler and replaying it is
 * safe: {@link CsrfTokenCache.send} drops the token, mints exactly one
 * replacement, and replays the request exactly once.
 */

/** The API's error handler answers a rejected token with this pair. */
const CSRF_REJECTION_STATUS = 403;
const CSRF_REJECTION_CODE = "csrf_validation_failed";

/**
 * Same bound as `CONTROL_PLANE_REQUEST_TIMEOUT_MS` in api.ts. It is repeated
 * rather than imported so this module stays a leaf: api.ts, the WhatsApp client
 * and the memory graph all depend on it, and importing back into api.ts would
 * close that cycle.
 */
const CSRF_MINT_TIMEOUT_MS = 15_000;

/**
 * A token could not be minted. Each client re-throws it as its own error type,
 * so the surface a caller sees never changes with which client happened to mint.
 */
export class CsrfTokenError extends Error {
  constructor(
    message: string,
    readonly status?: number,
  ) {
    super(message);
    this.name = "CsrfTokenError";
  }
}

export interface CsrfTokenCache {
  /**
   * Runs `perform` with a reusable CSRF token, minting one only when there is
   * none to reuse. Concurrent callers share a single mint. A rejected token is
   * refreshed once and `perform` runs exactly once more; a second rejection is
   * handed back to the caller rather than retried again.
   */
  readonly send: (
    perform: (token: string) => Promise<Response>,
  ) => Promise<Response>;
  /** Drops the cached token (sign-out, origin change, tests). */
  readonly reset: () => void;
}

async function isCsrfRejection(response: Response): Promise<boolean> {
  if (response.status !== CSRF_REJECTION_STATUS) return false;
  try {
    // `clone()` keeps the caller's response body readable: a 403 that turns out
    // not to be a CSRF rejection is returned untouched for the caller to parse.
    const payload: unknown = await response.clone().json();
    return (
      typeof payload === "object" &&
      payload !== null &&
      "error" in payload &&
      payload.error === CSRF_REJECTION_CODE
    );
  } catch {
    // A 403 with no readable body is not attributable to CSRF, and replaying it
    // blind would repeat a request the API may have refused for another reason.
    return false;
  }
}

interface MintedToken {
  readonly token: string;
  readonly generation: number;
}

export function createCsrfTokenCache(
  mint: () => Promise<string>,
): CsrfTokenCache {
  let token: string | null = null;
  // Distinguishes "the token I used was refused" from "someone already replaced
  // it": a whole wave of concurrent rejections then costs one replacement mint.
  let generation = 0;
  let flight: Promise<MintedToken> | null = null;

  async function mintOnce(): Promise<MintedToken> {
    try {
      const minted = await mint();
      token = minted;
      generation += 1;
      return { token: minted, generation };
    } finally {
      flight = null;
    }
  }

  function acquire(): Promise<MintedToken> {
    if (token !== null) return Promise.resolve({ token, generation });
    flight ??= mintOnce();
    return flight;
  }

  return {
    async send(perform) {
      const first = await acquire();
      const response = await perform(first.token);
      if (!(await isCsrfRejection(response))) return response;
      if (first.generation === generation) token = null;
      const replacement = await acquire();
      return perform(replacement.token);
    },
    reset() {
      token = null;
      flight = null;
    },
  };
}

async function mintCsrfToken(origin: string): Promise<string> {
  const controller = new AbortController();
  const timeout = globalThis.setTimeout(() => {
    controller.abort(
      new CsrfTokenError("The control-plane request timed out. Try again."),
    );
  }, CSRF_MINT_TIMEOUT_MS);
  let response: Response;
  try {
    // The mint is shared by every client and every concurrent request, so it
    // carries no caller AbortSignal: one cancelled view must not cancel the
    // token the others are waiting on. The timeout above still bounds it.
    response = await fetch(`${origin}/v1/security/csrf`, {
      credentials: "include",
      headers: { Accept: "application/json" },
      signal: controller.signal,
    });
  } finally {
    globalThis.clearTimeout(timeout);
  }
  if (!response.ok) {
    throw new CsrfTokenError(
      `The control plane returned ${response.status}.`,
      response.status,
    );
  }
  const payload: unknown = await response.json();
  if (
    typeof payload === "object" &&
    payload !== null &&
    "csrfToken" in payload &&
    typeof payload.csrfToken === "string" &&
    payload.csrfToken.length >= 32
  ) {
    return payload.csrfToken;
  }
  throw new CsrfTokenError("The control plane returned an invalid CSRF token.");
}

// Keyed by API origin so a token is never carried to a different one. All three
// browser clients (control plane, WhatsApp, memory graph) resolve the same
// entry, so one page load mints one token no matter which surface asks first.
// In practice the map holds a single entry: the origin comes from build config.
const cachesByOrigin = new Map<string, CsrfTokenCache>();

export function csrfTokenCacheFor(origin: string): CsrfTokenCache {
  const existing = cachesByOrigin.get(origin);
  if (existing) return existing;
  const cache = createCsrfTokenCache(() => mintCsrfToken(origin));
  cachesByOrigin.set(origin, cache);
  return cache;
}

/** Drops every cached token (sign-out, tests). */
export function resetCsrfTokenCaches(): void {
  cachesByOrigin.clear();
}
